import { visit } from 'unist-util-visit';
import type { Root, Element, Text } from 'hast';

/**
 * Rehype transform: rewrite ```mermaid fenced code blocks into
 * `<div class="mermaid" data-mermaid-source>…</div>` carrying the raw diagram
 * source as text.
 *
 * The client's mermaid runtime finds `.mermaid` nodes and renders them in
 * place; exporters rasterize them. Running this AFTER sanitize means the emitted
 * `div.mermaid` is trusted structural output, and mermaid itself renders with
 * its own `securityLevel: 'strict'` sanitizer on the client.
 */
export function rehypeMermaidSource() {
  return (tree: Root): void => {
    visit(tree, 'element', (node: Element, index, parent) => {
      if (node.tagName !== 'pre' || !parent || index === undefined) return;
      const code = node.children.find(
        (c): c is Element => c.type === 'element' && c.tagName === 'code',
      );
      if (!code) return;

      const classes = code.properties?.className;
      const classList = Array.isArray(classes) ? classes.map(String) : [];
      if (!classList.includes('language-mermaid')) return;

      const source = code.children
        .filter((c): c is Text => c.type === 'text')
        .map((c) => c.value)
        .join('');

      const replacement: Element = {
        type: 'element',
        tagName: 'div',
        properties: { className: ['mermaid'], 'data-mermaid-source': '' },
        children: [{ type: 'text', value: source }],
      };

      (parent.children as Element[])[index] = replacement;
    });
  };
}
